import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, ArrowRight, CheckCircle2, Sparkles, Music, Search, X, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

import { PlaylistBuilder, type Track } from "@/components/PlaylistBuilder";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import confetti from "canvas-confetti";
import { motion, AnimatePresence } from "framer-motion";
import { Progress } from "@/components/ui/progress";
import { YouTubePlayer } from "@/components/YouTubePlayer";
import { AudioProgressBar } from "@/components/AudioProgressBar";
import { useRef } from "react";
import { useAudioPlayer } from "@/contexts/AudioPlayerContext";
import { useSessionTracking } from "@/hooks/useSessionTracking";
import { AudioPermissionDialog } from "@/components/AudioPermissionDialog";
type SpotifyTrack = {
  id: string;
  name: string;
  artist: string;
  album: string;
  imageUrl: string;
  previewUrl: string | null;
  spotifyUrl: string;
  uri: string;
  durationMs?: number;
};
type EventStep = {
  eventIndex: number;
  occasion: string;
  vibe: string; // Display string (joined vibes)
  vibes?: string[]; // Array of vibes for AI
  guestCount: number;
  venueAddress: string;
  date: any;
  startTime: string;
  endTime: string;
  loadInTime?: string;
  selectedGenres: string[];
  selectedVibes: string[];
  songsAdded: number;
  totalDurationMs: number; // Total duration of all songs in milliseconds
  isComplete: boolean;
};
// All Spotify's official genre seeds (125+ genres)
const ALL_GENRES = [
  // Pop & Mainstream
  "Pop", "Synth-Pop", "Dance-Pop", "Indie-Pop", "Power-Pop", "Pop-Film",
  
  // Rock & Alternative
  "Rock", "Alt-Rock", "Indie-Rock", "Hard-Rock", "Psych-Rock", "Rock-n-Roll", 
  "Grunge", "Punk", "Pop-Punk", "Punk-Rock", "Emo", "Metal", "Heavy-Metal",
  "Black-Metal", "Death-Metal", "Metalcore", "Metal-Misc",
  
  // Hip-Hop & Rap
  "Hip-Hop", "Trap", "Rap", "Old-School", "Gangster-Rap",
  
  // R&B & Soul
  "R-n-B", "Soul", "Neo-Soul", "Funk", "Disco", "Groove",
  
  // Electronic & Dance
  "Electronic", "House", "Deep-House", "Progressive-House", "Tech-House",
  "Chicago-House", "Techno", "Detroit-Techno", "Minimal-Techno", "Trance", 
  "EDM", "Dance", "Drum-and-Bass", "Dubstep", "Post-Dubstep", "Electro",
  "Garage", "Club", "Breakbeat", "Hardstyle", "Hardcore", "IDM",
  
  // Jazz & Blues
  "Jazz", "Blues", "Smooth-Jazz", "Jazz-Funk",
  
  // Classical & Instrumental
  "Classical", "Piano", "Acoustic", "Instrumental", "Ambient", "New-Age",
  "Guitar", "Opera", "Soundtracks", "Show-Tunes",
  
  // Latin & World
  "Latin", "Latino", "Salsa", "Reggaeton", "Samba", "Bossanova", "Tango", 
  "Flamenco", "Brazilian", "Spanish", "French", "German", "Sertanejo",
  "Pagode", "Forro", "MPB",
  
  // Country & Folk
  "Country", "Folk", "Bluegrass", "Americana", "Honky-Tonk", "Rockabilly",
  
  // Reggae & Caribbean
  "Reggae", "Dancehall", "Ska", "Dub",
  
  // World & Cultural
  "Afrobeat", "Indian", "Bollywood", "Punjabi", "Hindi", "Tamil", "Telugu", 
  "Bengali", "Pakistani", "Marathi", "Gujarati", "Bhangra",
  "K-Pop", "J-Pop", "J-Dance", "J-Idol", "J-Rock", "Mandopop", "Cantopop", "C-Pop",
  "Iranian", "Turkish", "Arabic", "African", "Caribbean", "Malay",
  "Philippines-OPM", "Swedish", "British",
  
  // Mood & Vibe
  "Chill", "Party", "Romance", "Happy", "Sad", "Sleep", "Work-Out", 
  "Study", "Relaxing", "Rainy-Day", "Road-Trip", "Summer",
  
  // Gospel & Spiritual
  "Gospel", "Christian", "Worship",
  
  // Indie & Alternative
  "Indie", "Alternative", "New-Wave", "Post-Rock", "Goth", "Industrial",
  
  // Specialty & Other
  "Singer-Songwriter", "Songwriter", "Anime", "Comedy", "Children", "Kids",
  "Disney", "Holidays", "Movies", "Grindcore", "Trip-Hop", "World-Music"
];

// Popular/Curated genres to show by default (40 most common for events)
const DEFAULT_VISIBLE_GENRES = [
  "Pop", "Rock", "Hip-Hop", "R-n-B", "Electronic", "Jazz", "Classical",
  "Country", "Reggae", "Latin", "Indie", "Folk", "Blues", "Soul", "Funk",
  "Dance", "House", "EDM", "Acoustic", "Bollywood", "Indian", "Punjabi",
  "K-Pop", "Afrobeat", "Disco", "Techno", "Salsa", "Reggaeton", "Gospel",
  "Alternative", "Ambient", "Party", "Chill", "Romance", "Happy",
  "Indie-Pop", "Alt-Rock", "Dance-Pop", "Synth-Pop", "Bhangra"
];
const VibeBuilder = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    toast
  } = useToast();
  const { setOnAddToSelections, playTrack, togglePlayPause, stopPlayback, currentTrack, isPlaying } = useAudioPlayer();
  const {
    contactData,
    formData,
    initialEventIndex,
  } = location.state || {};
  const [currentStepIndex, setCurrentStepIndex] = useState(initialEventIndex ?? 0);
  const [eventSteps, setEventSteps] = useState<EventStep[]>([]);
  const [playingSongId, setPlayingSongId] = useState<string | null>(null);
  const [youtubeVideoId, setYoutubeVideoId] = useState<string | null>(null);
  const [isYoutubePlaying, setIsYoutubePlaying] = useState(false);
  const [audioRefs] = useState<Map<string, HTMLAudioElement>>(new Map());
  const [sessionId] = useState(() => sessionStorage.getItem('session_id') || crypto.randomUUID());
  const [showSummary, setShowSummary] = useState(false);
  
  // Session tracking
  const { trackInteraction } = useSessionTracking({
    sessionId,
    onSave: () => console.log('Session analytics saved'),
  });
  const [genreSearch, setGenreSearch] = useState("");
  const [showAllGenres, setShowAllGenres] = useState(false);
  const [audioProgress, setAudioProgress] = useState({
    currentTime: 0,
    duration: 30,
    progress: 0,
    trackName: '',
    artistName: '',
    imageUrl: '',
    source: 'spotify' as 'spotify' | 'youtube'
  });
  const [showProgress, setShowProgress] = useState(false);
  const audioProgressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const fadeOutTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [genreBlends, setGenreBlends] = useState<Map<number, any>>(new Map());
  const [hasTriggeredConfetti, setHasTriggeredConfetti] = useState<Set<number>>(new Set());
  const tasteProfileRef = useRef<HTMLDivElement>(null);
  const [isRequirementsMet, setIsRequirementsMet] = useState(false);
  const [songsVersion, setSongsVersion] = useState(0); // Tracks when songs change
  const lastMaxSongsToastRef = useRef<number>(0);
  const lastCleanupToastRef = useRef<number>(0);
  const [showAudioPermissionDialog, setShowAudioPermissionDialog] = useState(false);

  // Get audio player context
  const { 
    audioPermissionGranted, 
    requestAudioPermission, 
    setAudioPermissionGranted 
  } = useAudioPlayer();

  // Auto-grant audio permission for this page since audioConsented is always true
  useEffect(() => {
    if (!audioPermissionGranted) {
      requestAudioPermission();
      console.log('✅ Auto-granted audio permission for playlist builder');
    }
  }, [audioPermissionGranted, requestAudioPermission]);

  // Initialize event steps
  useEffect(() => {
    const loadEventSteps = async () => {
      let currentContactData = contactData;
      let currentFormData = formData;
      
      console.log('🔍 Loading event steps with data:', { currentContactData, currentFormData });
      
      if (!currentContactData || !currentFormData) {
        console.log('No contact data found, redirecting to home');
        navigate("/");
        return;
      }
      
      // Try to load saved vibe builder data from database
      const { data: savedDraft } = await supabase
        .from('contact_form_drafts')
        .select('form_data')
        .eq('session_id', sessionId)
        .maybeSingle();
      
      const savedEventSteps = (savedDraft?.form_data as any)?.eventSteps;
      console.log('Loaded saved event steps:', savedEventSteps);
      
      const events = currentFormData?.eventDates || [{
        occasion: currentFormData?.occasion,
        vibes: currentFormData?.vibes || currentFormData?.vibe ? [currentFormData?.vibe] : [],
        guestCount: currentFormData?.guestCount,
        venueAddress: currentFormData?.venueAddress,
        date: currentFormData?.eventDate,
        startTime: currentFormData?.eventStartTime,
        endTime: currentFormData?.eventEndTime
      }];
      
      console.log('📋 Events array:', events);
      
      const steps: EventStep[] = events.map((event: any, index: number) => {
        const savedStep = savedEventSteps?.[index];
        const vibesArray = event.vibes || [];
        const vibeDisplay = vibesArray.length > 0 ? vibesArray.join(', ') : 'Not specified';
        
        const step = {
          eventIndex: index,
          occasion: event.occasion,
          vibe: vibeDisplay, // Display string for UI
          vibes: vibesArray, // Array for AI analysis
          guestCount: event.guestCount || currentFormData?.guestCount,
          venueAddress: event.venueAddress || currentFormData?.venueAddress,
          date: event.date,
          startTime: event.startTime,
          endTime: event.endTime,
          loadInTime: event.loadInTime,
          selectedGenres: savedStep?.selectedGenres || [],
          selectedVibes: savedStep?.selectedVibes || [],
          songsAdded: 0,
          totalDurationMs: 0,
          isComplete: false
        };
        console.log(`📌 Step ${index}:`, step);
        return step;
      });
      
      setEventSteps(steps);
    };
    
    loadEventSteps();
  }, [navigate, sessionId, contactData, formData]);
  
  // Session persistence removed
  
  useEffect(() => {
    sessionStorage.setItem('session_id', sessionId);
  }, [sessionId]);

  // Confetti and smooth scroll when 3 genres are selected
  useEffect(() => {
    const currentStep = eventSteps[currentStepIndex];
    if (currentStep?.selectedGenres.length === 3 && !hasTriggeredConfetti.has(currentStepIndex)) {
      // Trigger confetti
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['hsl(var(--primary))', 'hsl(var(--accent))', 'hsl(var(--secondary))']
      });
      
      // Smooth scroll to taste profile
      setTimeout(() => {
        tasteProfileRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
      
      // Mark this step as having triggered confetti
      setHasTriggeredConfetti(prev => new Set(prev).add(currentStepIndex));
    }
  }, [eventSteps, currentStepIndex, hasTriggeredConfetti]);

  // Load songs per event from database
  const loadSongsForEvents = async () => {
    try {
      const MAX_SONGS_PER_EVENT = 25;
      
      const {
        data,
        error
      } = await supabase.from('favorite_songs').select('id, event_index, created_at').eq('session_id', sessionId).order('created_at', { ascending: true });
      
      if (error) throw error;
      
      if (data) {
        // Group songs by event_index
        const songsByEvent = data.reduce((acc: any, song: any) => {
          const idx = song.event_index || 0;
          if (!acc[idx]) acc[idx] = [];
          acc[idx].push(song);
          return acc;
        }, {});
        
        // Check each event and remove excess songs (keep oldest 25)
        let totalRemoved = 0;
        const eventsWithExcess: number[] = [];
        
        for (const [eventIdx, songs] of Object.entries(songsByEvent)) {
          const songList = songs as any[];
          if (songList.length > MAX_SONGS_PER_EVENT) {
            // Keep the oldest 25 songs, remove the rest
            const songsToRemove = songList.slice(MAX_SONGS_PER_EVENT);
            const idsToRemove = songsToRemove.map(s => s.id);
            
            console.log(`Event ${eventIdx} has ${songList.length} songs, removing ${songsToRemove.length} excess songs`);
            
            const { error: deleteError } = await supabase
              .from('favorite_songs')
              .delete()
              .in('id', idsToRemove);
            
            if (deleteError) {
              console.error('Error removing excess songs:', deleteError);
            } else {
              totalRemoved += songsToRemove.length;
              eventsWithExcess.push(Number(eventIdx) + 1);
            }
          }
        }
        
        // Show a single toast for all cleanups (debounced)
        if (totalRemoved > 0) {
          const now = Date.now();
          const timeSinceLastToast = now - lastCleanupToastRef.current;
          
          if (timeSinceLastToast > 3000) {
            const eventList = eventsWithExcess.length > 1 
              ? `events ${eventsWithExcess.join(', ')}`
              : `event ${eventsWithExcess[0]}`;
            
            toast({
              title: "Songs Cleaned Up",
              description: `Removed ${totalRemoved} excess song${totalRemoved !== 1 ? 's' : ''} from ${eventList} (max 25 per event)`,
            });
            lastCleanupToastRef.current = now;
          }
        }
        
        // Reload to get accurate counts and duration after cleanup
        const { data: updatedData } = await supabase
          .from('favorite_songs')
          .select('event_index, duration_ms')
          .eq('session_id', sessionId);
        
        if (updatedData) {
          const eventData = updatedData.reduce((acc: any, song: any) => {
            const idx = song.event_index || 0;
            if (!acc[idx]) {
              acc[idx] = { count: 0, totalDurationMs: 0 };
            }
            acc[idx].count += 1;
            acc[idx].totalDurationMs += song.duration_ms || 0;
            return acc;
          }, {});
          
          setEventSteps(prev => prev.map((step, idx) => ({
            ...step,
            songsAdded: eventData[idx]?.count || 0,
            totalDurationMs: eventData[idx]?.totalDurationMs || 0
          })));
        }
      }
    } catch (error) {
      console.error('Error loading songs:', error);
    }
  };

  useEffect(() => {
    if (eventSteps.length > 0) {
      loadSongsForEvents();
    }
  }, [sessionId, eventSteps.length]);

  // Real-time subscription for song updates
  useEffect(() => {
    if (!sessionId) return;

    const channel = supabase
      .channel('favorite-songs-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'favorite_songs',
          filter: `session_id=eq.${sessionId}`
        },
        (payload) => {
          console.log('Real-time song update:', payload);
          loadSongsForEvents();
          // Trigger requirements check
          setSongsVersion(v => v + 1);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionId]);
  const triggerConfetti = useCallback(() => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: {
        y: 0.6
      }
    });
  }, []);
  
  const handleAddSong = useCallback(async (song: SpotifyTrack, eventIndex?: number) => {
    const targetEventIndex = eventIndex ?? currentStepIndex;
    
    try {
      // Check current song count for this event
      const { data: existingSongs, error: countError } = await supabase
        .from('favorite_songs')
        .select('id', { count: 'exact' })
        .eq('session_id', sessionId)
        .eq('event_index', targetEventIndex);
      
      if (countError) {
        console.error('Error checking song count:', countError);
      }
      
      const currentCount = existingSongs?.length || 0;
      const MAX_SONGS = 25;
      
      // Enforce 25-song limit with debounced toast (show only once per 3 seconds)
      if (currentCount >= MAX_SONGS) {
        const now = Date.now();
        const timeSinceLastToast = now - lastMaxSongsToastRef.current;
        
        // Only show toast if it hasn't been shown in the last 3 seconds
        if (timeSinceLastToast > 3000) {
          toast({
            title: "Maximum Songs Reached",
            description: `You've reached the maximum of ${MAX_SONGS} songs for this event.`,
            variant: "destructive"
          });
          lastMaxSongsToastRef.current = now;
        }
        return;
      }
      
      // Include authenticated user if available (safer RLS)
      const { data: { session } } = await supabase.auth.getSession();

      const { error } = await supabase.from('favorite_songs').insert({
        user_id: session?.user?.id || null,
        session_id: sessionId,
        song_title: song.name,
        artist: song.artist,
        album: song.album,
        spotify_uri: song.uri,
        image_url: song.imageUrl,
        preview_url: song.previewUrl,
        event_index: targetEventIndex,
        duration_ms: song.durationMs || 180000 // Default to 3 minutes if not provided
      });
      
      console.log('💾 Song saved:', {
        songName: song.name,
        durationMs: song.durationMs || 180000,
        durationMinutes: ((song.durationMs || 180000) / 60000).toFixed(2)
      });
      if (error) {
        // Ignore duplicate errors (constraint violation)
        if (error.code === '23505') {
          console.log('Song already exists, skipping...');
          return;
        }
        throw error;
      }
      setEventSteps(prev => prev.map((step, idx) => idx === targetEventIndex ? {
        ...step,
        songsAdded: step.songsAdded + 1,
        totalDurationMs: step.totalDurationMs + (song.durationMs || 180000)
      } : step));
      const newSongCount = eventSteps[targetEventIndex].songsAdded + 1;
      if (newSongCount === 1) {
        triggerConfetti();
      } else if (newSongCount === 3) {
        triggerConfetti();
      }
    } catch (error) {
      console.error('Error adding song:', error);
      toast({
        title: "Error",
        description: "Failed to add song",
        variant: "destructive"
      });
    }
  }, [currentStepIndex, sessionId, eventSteps, toast, triggerConfetti]);
  
  // Set up audio player callback for AI suggestions
  useEffect(() => {
    setOnAddToSelections(async (track) => {
      await handleAddSong({
        id: track.id,
        name: track.name,
        artist: track.artist,
        album: track.album,
        imageUrl: track.imageUrl,
        previewUrl: track.previewUrl,
        spotifyUrl: track.spotifyUrl,
        uri: track.uri,
        durationMs: track.durationMs,
      }, currentStepIndex);
    });
  }, [handleAddSong, currentStepIndex, setOnAddToSelections]);
  
  const handlePlayPause = async (track: Track) => {
    console.log('🎵 handlePlayPause called for:', track.name);
    console.log('🎵 currentTrack:', currentTrack?.name, 'isPlaying:', isPlaying);
    
    // If same track is playing, toggle play/pause
    if (currentTrack?.id === track.id && isPlaying) {
      console.log('⏸️ Toggling play/pause for same track');
      togglePlayPause();
      return;
    }
    
    // Otherwise play the new track using context (which uses preloaded audio!)
    console.log('▶️ Playing new track:', track.name, 'Preview URL:', track.previewUrl);
    playTrack(track);
  };
  const toggleGenre = (genre: string) => {
    setEventSteps(prev => {
      const updated = prev.map((step, idx) => {
        if (idx === currentStepIndex) {
          const newGenres = step.selectedGenres.includes(genre) ? step.selectedGenres.filter(g => g !== genre) : [...step.selectedGenres, genre];
          return {
            ...step,
            selectedGenres: newGenres
          };
        }
        return step;
      });
      
      // Save to database
      savePlaylistData(updated);
      return updated;
    });
  };

  const clearAllGenres = () => {
    setEventSteps(prev => {
      const updated = prev.map((step, idx) => {
        if (idx === currentStepIndex) {
          return {
            ...step,
            selectedGenres: []
          };
        }
        return step;
      });
      
      // Save to database
      savePlaylistData(updated);
      return updated;
    });
    toast({
      title: "Genres Cleared",
      description: "All genre selections have been removed"
    });
  };
  const handleVibesUpdate = (vibes: string[]) => {
    setEventSteps(prev => {
      const updated = prev.map((step, idx) => {
        if (idx === currentStepIndex) {
          return {
            ...step,
            selectedVibes: vibes
          };
        }
        return step;
      });
      
      // Save to database
      savePlaylistData(updated);
      return updated;
    });
  };
  
  // Save playlist data to database (merges with existing form data to preserve ContactForm data)
  const savePlaylistData = async (steps: EventStep[]) => {
    try {
      // First, fetch existing form data to merge with
      const { data: existingDraft } = await supabase
        .from('contact_form_drafts')
        .select('form_data')
        .eq('session_id', sessionId)
        .maybeSingle();
      
      // Merge eventSteps with existing form data (preserves ContactForm fields)
      const existingFormData = (existingDraft?.form_data as Record<string, unknown>) || {};
      const mergedData = {
        ...existingFormData,
        eventSteps: steps // Update only eventSteps, keep everything else
      };
      
      console.log('Saving playlist data to database (merged):', mergedData);
      
      // Save to contact_form_drafts
      const { error } = await supabase
        .from('contact_form_drafts')
        .upsert({
          session_id: sessionId,
          form_data: mergedData
        }, {
          onConflict: 'session_id'
        });
      
      if (error) {
        console.error('Error saving to database:', error);
        throw error;
      }
      
      console.log('Successfully saved playlist data');
    } catch (error) {
      console.error('Error saving playlist data:', error);
    }
  };
  const canProceedToNext = () => {
    const currentStep = eventSteps[currentStepIndex];
    if (!currentStep) return false;

    const genreCount = currentStep.selectedGenres.length;
    const trackCount = currentStep.songsAdded;

    const hasEnoughGenres = genreCount >= 3;
    const hasEnoughTracks = trackCount >= 5;

    console.log('📊 Step completion check:', {
      eventIndex: currentStepIndex,
      genreCount,
      trackCount,
      hasEnoughGenres,
      hasEnoughTracks,
    });

    return hasEnoughGenres && hasEnoughTracks;
  };
  const handleNext = () => {
    const canProceed = canProceedToNext();
    if (!canProceed) {
      toast({
        title: "Complete This Step",
        description: "Please select at least 3 genres and add at least 5 tracks before continuing.",
        variant: "destructive"
      });
      return;
    }

    // Mark current step as complete
    setEventSteps(prev => {
      const updated = prev.map((step, idx) => idx === currentStepIndex ? {
        ...step,
        isComplete: true
      } : step);
      
      // Save to database
      savePlaylistData(updated);
      return updated;
    });
    
    // Show audio permission dialog when completing first event (if not already granted)
    if (currentStepIndex === 0 && !audioPermissionGranted) {
      setShowAudioPermissionDialog(true);
      return;
    }
    
    if (currentStepIndex < eventSteps.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      // All events complete - stop audio and navigate to shop
      stopPlayback();
      navigate('/shop', {
        state: {
          contactData,
          formData,
          initialEventIndex: currentStepIndex,
        },
      });
    }
  };
  
  const handleNextClick = () => handleNext();

  useEffect(() => {
    if (eventSteps.length > 0 && currentStepIndex < eventSteps.length) {
      setIsRequirementsMet(canProceedToNext());
    }
  }, [eventSteps, currentStepIndex]);
  
  const analyzeAllEventGenreBlends = async () => {
    const newGenreBlends = new Map<number, any>();
    
    for (let i = 0; i < eventSteps.length; i++) {
      const step = eventSteps[i];
      try {
        // Fetch songs for this event
        const { data: songs } = await supabase
          .from('favorite_songs')
          .select('*')
          .eq('session_id', sessionId)
          .eq('event_index', i)
          .order('created_at', { ascending: false });

        if (songs && songs.length > 0) {
          const eventContext = {
            occasion: step.occasion,
            vibe: step.vibe,
            guestCount: step.guestCount,
            venueAddress: step.venueAddress,
            date: step.date,
            startTime: step.startTime,
            endTime: step.endTime,
            loadInTime: step.loadInTime
          };

          const { data: genreBlendData, error } = await supabase.functions.invoke('classify-genre-blend', {
            body: {
              playlist: songs.map(s => ({
                name: s.song_title,
                artist: s.artist,
                id: s.spotify_uri?.replace('spotify:track:', '') || s.id
              })),
              eventContext,
              vibes: step.selectedVibes,
              selectedGenres: step.selectedGenres,
              weather: {
                season: 'unknown',
                condition: 'clear',
                temperature: 70
              },
              spotifyConnected: false
            }
          });

          if (!error && genreBlendData) {
            newGenreBlends.set(i, genreBlendData);
          }
        }
      } catch (error) {
        console.error(`Error analyzing genre blend for event ${i}:`, error);
      }
    }

    setGenreBlends(newGenreBlends);
    setShowSummary(true);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const handleEditEvent = (eventIndex: number) => {
    setShowSummary(false);
    setCurrentStepIndex(eventIndex);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const handleContinueToQA = () => {
    navigate("/contact-qa", {
      state: {
        contactData,
        formData,
        vibeData: eventSteps
      }
    });
  };
  if (eventSteps.length === 0) {
    return null;
  }

  // Show summary view
  if (showSummary) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 max-w-7xl">
            {/* Header */}
            <div className="mb-6 text-center">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent mb-2">
                Review Your Music Profile
              </h1>
              <p className="text-muted-foreground">All events completed! Review and edit if needed, then continue.</p>
            </div>

            {/* Summary Cards */}
            <div className="space-y-4 mb-6">
              {eventSteps.map((step, idx) => <Card key={idx} className="p-6 border-2 border-primary/20">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <Badge variant="default" className="text-xs">Event {idx + 1}</Badge>
                        <h2 className="text-xl font-bold">{step.occasion}</h2>
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                        <div>
                          <p className="text-muted-foreground text-xs mb-1">Theme/Vibe</p>
                          <Badge variant="secondary" className="font-medium">
                            {step.vibe || 'Not specified'}
                          </Badge>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-xs mb-1">Guests</p>
                          <p className="font-medium">{step.guestCount}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-xs mb-1">Time</p>
                          <p className="font-medium">{step.startTime} - {step.endTime}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Songs Added</p>
                          <p className="font-medium">{step.songsAdded} songs</p>
                        </div>
                      </div>

                      <div className="space-y-2">
                        {step.selectedGenres.length > 0 && <div>
                            <p className="text-xs text-muted-foreground mb-1">Favorite Genres:</p>
                            <div className="flex flex-wrap gap-1">
                              {step.selectedGenres.map(genre => <Badge key={genre} variant="outline" className="text-xs">{genre}</Badge>)}
                            </div>
                          </div>}
                        
                        {step.selectedVibes.length > 0 && <div>
                            <p className="text-xs text-muted-foreground mb-1">Selected Vibes:</p>
                            <div className="flex flex-wrap gap-1">
                              {step.selectedVibes.map(vibe => <Badge key={vibe} variant="secondary" className="text-xs">{vibe}</Badge>)}
                            </div>
                          </div>}
                        
                        {/* Genre Blend Analysis */}
                        {genreBlends.get(idx) && <div className="mt-4 p-4 bg-gradient-to-r from-primary/5 to-accent/5 rounded-lg border border-primary/20">
                            <div className="flex items-center gap-2 mb-2">
                              <Sparkles className="h-4 w-4 text-primary" />
                              <p className="text-sm font-semibold text-foreground">AI Genre Analysis</p>
                            </div>
                            <p className="text-xs text-muted-foreground mb-3">
                              {genreBlends.get(idx).genreBlendDescription}
                            </p>
                            <div className="space-y-2">
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">Primary Genres:</p>
                                <div className="flex flex-wrap gap-1">
                                  {genreBlends.get(idx).primaryGenres?.map((genre: string) => 
                                    <Badge key={genre} variant="default" className="text-xs capitalize">
                                      {genre}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              {genreBlends.get(idx).secondaryGenres?.length > 0 && <div>
                                <p className="text-xs text-muted-foreground mb-1">Secondary Genres:</p>
                                <div className="flex flex-wrap gap-1">
                                  {genreBlends.get(idx).secondaryGenres?.map((genre: string) => 
                                    <Badge key={genre} variant="outline" className="text-xs capitalize">
                                      {genre}
                                    </Badge>
                                  )}
                                </div>
                              </div>}
                            </div>
                          </div>}
                      </div>
                    </div>

                    <Button variant="outline" onClick={() => handleEditEvent(idx)}>
                      Edit
                    </Button>
                  </div>
                </Card>)}
            </div>

            {/* Action Buttons */}
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <Button variant="outline" onClick={() => handleEditEvent(eventSteps.length - 1)}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Last Event
                </Button>

                <Button onClick={handleContinueToQA} className="gap-2">
                  Continue to Q&A
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </Card>
          </div>
        </div>
      );
    }
  const currentStep = eventSteps[currentStepIndex];
  const progress = (currentStepIndex + 1) / eventSteps.length * 100;

  // Build event context for PlaylistBuilder
  const eventContext = {
    events: eventSteps.map(step => ({
      occasion: step.occasion,
      vibe: step.vibe,
      guestCount: step.guestCount,
      venueAddress: step.venueAddress,
      date: step.date,
      startTime: step.startTime,
      endTime: step.endTime,
      loadInTime: step.loadInTime
    })),
    season: formData?.season,
    culturalNotes: formData?.culturalNotes
  };
  return (
    <>
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 max-w-7xl">
          {/* Header */}
          <div className="mb-6 flex items-center justify-between">
            <Button 
              variant="ghost" 
              onClick={() => {
                if (currentStepIndex > 0) {
                  // Go to previous event
                  setCurrentStepIndex(currentStepIndex - 1);
                } else {
                  // We're on event 1, go back to Event Dates & Times (formStep 2 in ContactForm)
                  navigate("/", { state: { formData, formStep: 2 } });
                }
              }} 
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              {currentStepIndex > 0 ? `Back to Event ${currentStepIndex}` : 'Back to Event Dates'}
            </Button>
            <div className="text-center flex-1">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                Build Your Music Profile
              </h1>
              <p className="text-sm text-muted-foreground">Step {currentStepIndex + 1} of {eventSteps.length}</p>
            </div>
            <div className="w-24" />
          </div>

          {/* Overall Progress */}
          

          {/* Current Event Details */}
          <AnimatePresence mode="wait">
            <motion.div key={currentStepIndex} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3
          }}>
              <Card className="mb-6 p-6 border-2 border-primary/20">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-full bg-primary/10">
                    <Music className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-2xl font-bold">Event {currentStepIndex + 1}: {currentStep.occasion}</h2>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground text-xs mb-1">Theme/Vibe</p>
                        <Badge variant="secondary" className="font-medium">
                          {currentStep.vibe || 'Not specified'}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs mb-1">Guests</p>
                        <p className="font-medium">{currentStep.guestCount}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs mb-1">Time</p>
                        <p className="font-medium">{currentStep.startTime} - {currentStep.endTime}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Songs Added</p>
                        <p className="font-medium">{currentStep.songsAdded} songs</p>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Genre Selection */}
              <Card className={`mb-6 p-6 transition-all duration-500 ${
                currentStep.selectedGenres.length < 3
                  ? 'ring-4 ring-primary/50 shadow-[0_0_30px_rgba(var(--primary),0.3)] animate-pulse' 
                  : ''
              }`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Sparkles className={`h-5 w-5 ${currentStep.selectedGenres.length < 3 ? 'text-primary animate-pulse' : 'text-primary'}`} />
                    Select Your Favorite Genres <span className="text-destructive">*</span>
                  </h3>
                  {currentStep.selectedGenres.length > 0 && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={clearAllGenres}
                      className="gap-1 text-destructive hover:text-destructive"
                    >
                      <X className="h-4 w-4" />
                      Clear All
                    </Button>
                  )}
                </div>
                
                <p className="text-sm text-muted-foreground mb-4">
                  {currentStep.selectedGenres.length < 3
                    ? currentStep.selectedGenres.length === 0
                      ? "👉 Start here! Choose at least 3 genres to help us curate the perfect music for your event"
                      : `Choose ${3 - currentStep.selectedGenres.length} more genre${3 - currentStep.selectedGenres.length !== 1 ? 's' : ''} (minimum 3 required)`
                    : "Great! 3 genres selected ✓"
                  }
                </p>

                {/* Search Input */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search genres..."
                    value={genreSearch}
                    onChange={(e) => setGenreSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>

                <div className="flex flex-wrap gap-2">
                  {(genreSearch 
                    ? ALL_GENRES.filter(genre => genre.toLowerCase().includes(genreSearch.toLowerCase()))
                    : showAllGenres ? ALL_GENRES : DEFAULT_VISIBLE_GENRES
                  ).map(genre => {
                      const isSelected = currentStep.selectedGenres.includes(genre);
                      return <Button 
                        key={genre} 
                        variant={isSelected ? "default" : "outline"} 
                        size="sm" 
                        onClick={() => toggleGenre(genre)} 
                        className="transition-all"
                      >
                        {genre}
                      </Button>;
                    })}
                </div>

                {/* Show More/Less Button */}
                {!genreSearch && (
                  <div className="flex justify-center mt-4">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setShowAllGenres(!showAllGenres)}
                      className="gap-2"
                    >
                      {showAllGenres ? (
                        <>Show Less Genres</>
                      ) : (
                        <>Show All {ALL_GENRES.length} Genres</>
                      )}
                    </Button>
                  </div>
                )}

                {genreSearch && ALL_GENRES.filter(g => g.toLowerCase().includes(genreSearch.toLowerCase())).length === 0 && (
                  <p className="text-sm text-muted-foreground text-center mt-4">
                    No genres found matching "{genreSearch}"
                  </p>
                )}

                {currentStep.selectedGenres.length > 0 && <motion.p initial={{
                opacity: 0,
                y: -10
              }} animate={{
                opacity: 1,
                y: 0
              }} className="text-xs text-muted-foreground mt-3">
                    ✓ {currentStep.selectedGenres.length} genre{currentStep.selectedGenres.length !== 1 ? 's' : ''} selected
                  </motion.p>}
              </Card>

              {/* Playlist Builder with Floating Player */}
              <div className="relative">
                {currentStep.selectedGenres.length >= 3 && currentStep.songsAdded === 0 && (
                  <motion.div
                    className="absolute -inset-4 bg-gradient-to-r from-primary/20 via-accent/20 to-primary/20 rounded-xl blur-xl z-0"
                    animate={{
                      opacity: [0.5, 0.8, 0.5],
                      scale: [1, 1.05, 1],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  />
                )}
                <motion.div
                  ref={tasteProfileRef}
                  initial={{ opacity: 0.5, y: 20 }}
                  animate={{ 
                    opacity: currentStep.selectedGenres.length >= 3 ? 1 : 0.5,
                    y: currentStep.selectedGenres.length >= 3 ? 0 : 20
                  }}
                  transition={{ duration: 0.5 }}
                  className={
                    currentStep.selectedGenres.length >= 3 && currentStep.songsAdded === 0 
                      ? "relative z-10 ring-4 ring-primary shadow-[0_0_50px_hsl(var(--primary)/0.5)] rounded-lg transition-all duration-500" 
                      : "relative z-10 transition-all duration-500"
                  }
                >
                  {/* Audio Progress Bar - Positioned at top right of taste profile */}
                  <AnimatePresence>
                    {showProgress && playingSongId && (
                      <AudioProgressBar
                        trackName={audioProgress.trackName}
                        artistName={audioProgress.artistName}
                        imageUrl={audioProgress.imageUrl}
                        progress={audioProgress.progress}
                        duration={audioProgress.duration}
                        currentTime={audioProgress.currentTime}
                        source={audioProgress.source}
                        isPlaying={audioProgress.source === 'spotify' ? !audioRefs.get(playingSongId)?.paused : isYoutubePlaying}
                        onStop={() => {
                          // Stop Spotify playback
                          if (playingSongId) {
                            const audio = audioRefs.get(playingSongId);
                            if (audio) {
                              audio.pause();
                              audio.currentTime = 0;
                              audio.volume = 0.5;
                            }
                          }
                          
                          // Stop YouTube
                          setYoutubeVideoId(null);
                          setIsYoutubePlaying(false);
                          
                          // Clear states
                          setPlayingSongId(null);
                          setShowProgress(false);
                          if (audioProgressIntervalRef.current) {
                            clearInterval(audioProgressIntervalRef.current);
                          }
                          if (fadeOutTimeoutRef.current) {
                            clearTimeout(fadeOutTimeoutRef.current);
                          }
                        }}
                        onTogglePlayPause={() => {
                          if (audioProgress.source === 'spotify') {
                            const audio = audioRefs.get(playingSongId);
                            if (audio) {
                              if (audio.paused) {
                                audio.play();
                              } else {
                                audio.pause();
                              }
                            }
                          } else {
                            setIsYoutubePlaying(!isYoutubePlaying);
                          }
                        }}
                      />
                    )}
                  </AnimatePresence>
                  
                  <PlaylistBuilder 
                    onAddSong={handleAddSong} 
                    playingSongId={currentTrack?.id || null} 
                    onPlayToggle={handlePlayPause} 
                    audioConsented={true}
                    eventContext={eventContext} 
                    onVibesUpdate={handleVibesUpdate}
                    selectedGenres={currentStep.selectedGenres} 
                    requireVibes={true}
                    shouldGlow={currentStep.selectedGenres.length >= 3 && currentStep.songsAdded === 0}
                    eventIndex={currentStepIndex}
                    onTrackCountChange={(count, eventIndex) => {
                      setEventSteps(prev => prev.map((step, idx) =>
                        idx === eventIndex
                          ? { ...step, songsAdded: count }
                          : step
                      ));
                    }}
                  />
                </motion.div>
              </div>

              {/* Navigation */}
              <Card className="mt-6 p-6">
                <div className="flex items-center justify-between">
                  <div className="w-32" />

                  <div className="text-center">
                    {isRequirementsMet ? (
                      <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: "spring", stiffness: 200, damping: 15 }}
                        className="flex items-center gap-2 text-primary font-medium justify-center"
                      >
                        <motion.div
                          animate={{ 
                            rotate: [0, 360],
                            scale: [1, 1.2, 1]
                          }}
                          transition={{ 
                            duration: 0.6,
                            ease: "easeInOut"
                          }}
                        >
                          <CheckCircle2 className="w-5 h-5" />
                        </motion.div>
                        <motion.span
                          initial={{ x: -10, opacity: 0 }}
                          animate={{ x: 0, opacity: 1 }}
                          transition={{ delay: 0.2 }}
                        >
                          Ready to continue!
                        </motion.span>
                      </motion.div>
                    ) : (
                      <motion.div 
                        className="flex items-center gap-2 text-muted-foreground justify-center"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                      >
                        <AlertCircle className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          {(() => {
                            const currentEvent = eventSteps[currentStepIndex];
                            const missing: string[] = [];
                            
                            if ((currentEvent?.selectedGenres?.length || 0) < 3) {
                              const needed = 3 - (currentEvent?.selectedGenres?.length || 0);
                              missing.push(`${needed} more genre${needed !== 1 ? 's' : ''}`);
                            }
                            
                            // Always show the track requirement if not complete
                            if (!isRequirementsMet) {
                              const requiredTracks = 5;
                              const currentTracks = currentEvent?.songsAdded || 0;
                              const tracksNeeded = Math.max(0, requiredTracks - currentTracks);
                              if (tracksNeeded > 0) {
                                missing.push(`${tracksNeeded} more track${tracksNeeded !== 1 ? 's' : ''}`);
                              }
                            }
                            
                            if (missing.length === 0) {
                              return 'Checking requirements...';
                            }
                            
                            return `Need: ${missing.join(', ')}`;
                          })()}
                        </span>
                      </motion.div>
                    )}
                  </div>

                  <motion.div
                    animate={isRequirementsMet ? {
                      boxShadow: [
                        "0 0 20px hsl(var(--primary) / 0.3)",
                        "0 0 40px hsl(var(--primary) / 0.5)",
                        "0 0 20px hsl(var(--primary) / 0.3)"
                      ]
                    } : {}}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    className="rounded-md"
                  >
                    <Button 
                      onClick={handleNextClick} 
                      disabled={!isRequirementsMet}
                      className="gap-2 min-w-[160px]"
                    >
                      {currentStepIndex < eventSteps.length - 1 ? <>
                          Next Event
                          <ArrowRight className="h-4 w-4" />
                        </> : <>
                          Explore Packages
                          <ArrowRight className="h-4 w-4" />
                        </>}
                    </Button>
                  </motion.div>
                </div>
              </Card>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
      
      {/* YouTube Player for fallback when no Spotify preview */}
      {youtubeVideoId && (
        <YouTubePlayer 
          videoId={youtubeVideoId}
          isPlaying={isYoutubePlaying}
          maxDuration={30}
          onProgress={(currentTime, duration) => {
            setAudioProgress(prev => ({
              ...prev,
              currentTime,
              duration,
              progress: (currentTime / duration) * 100
            }));
          }}
          onEnded={() => {
            setYoutubeVideoId(null);
            setIsYoutubePlaying(false);
            setPlayingSongId(null);
            setShowProgress(false);
          }}
        />
      )}
      
      {/* Audio Permission Dialog */}
      <AudioPermissionDialog
        open={showAudioPermissionDialog}
        onAccept={() => {
          requestAudioPermission();
          setShowAudioPermissionDialog(false);
          
          toast({
            title: "Audio Enabled",
            description: "You can now play music previews!",
          });
          
          // Continue to next event
          if (currentStepIndex < eventSteps.length - 1) {
            setCurrentStepIndex(prev => prev + 1);
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
          } else {
            stopPlayback();
            navigate('/shop', {
              state: {
                contactData,
                formData,
                initialEventIndex: currentStepIndex,
              },
            });
          }
        }}
        onDecline={() => {
          setShowAudioPermissionDialog(false);
          setAudioPermissionGranted(false);
          
          toast({
            title: "Audio Disabled",
            description: "Music previews won't play. You can enable this later in settings.",
            variant: "default",
          });
          
          // Continue to next event even if declined
          if (currentStepIndex < eventSteps.length - 1) {
            setCurrentStepIndex(prev => prev + 1);
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
          } else {
            stopPlayback();
            navigate('/shop', {
              state: {
                contactData,
                formData,
                initialEventIndex: currentStepIndex,
              },
            });
          }
        }}
      />
    </>
  );
};
export default VibeBuilder;